import React from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { EditIcon, DeleteIcon } from '../components/icons';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const assetUrl = (relPath) => (relPath ? `${API_ORIGIN}/uploads/${relPath}` : null);

const MONTH_ABBR = ['JN', 'FB', 'MR', 'AP', 'MY', 'JU', 'JL', 'AG', 'SP', 'OC', 'NV', 'DC'];

function financialYearShort(date) {
  const y = date.getFullYear();
  const m = date.getMonth();
  const startYear = m >= 3 ? y : y - 1;
  return `${String(startYear).slice(-2)}${String(startYear + 1).slice(-2)}`;
}

// Mirrors the backend's buildInvoiceNumberPrefix exactly, so the preview
// shown here matches what the server will actually generate.
function buildInvoiceNumberPrefix(invoiceDate, invoiceCode) {
  if (!invoiceDate) return '';
  const date = new Date(invoiceDate);
  if (Number.isNaN(date.getTime())) return '';
  const fy = financialYearShort(date);
  const mon = MONTH_ABBR[date.getMonth()];
  const dd = String(date.getDate()).padStart(2, '0');
  const prefix = invoiceCode || 'KTS';
  return `${prefix}${fy}-${mon}-${dd}-`;
}

const EMPTY_GUEST = () => ({
  guest_name: '', check_in: '', check_out: '', no_of_rooms: 1, no_of_nights: 1,
  confirmation_no: '', basic_amount: 0, commission_percent: 10
});

// Bulk Booking uses a simpler row: just a description and an amount.
// Reuses the same field names as the guest-entry shape (guest_name holds
// the description, basic_amount holds the amount, commission_percent is
// fixed at 100 so the billed "Comm Amt" equals the amount entered) so the
// backend needs no changes for this template.
const EMPTY_BOOKING = () => ({
  guest_name: '', check_in: '', check_out: '', no_of_rooms: 1, no_of_nights: 1,
  confirmation_no: '', basic_amount: 0, commission_percent: 100
});

const EMPTY_FORM = {
  invoice_date: new Date().toISOString().slice(0, 10),
  company_id: '',
  registration_id: '',
  invoice_type: '',
  hotel_id: '',
  invoice_seq: '',
  tax_percent: 18,
  notes: '',
  guests: [EMPTY_GUEST()]
};

const STEPS = ['Company', 'State', 'Template', 'Details'];

const TEMPLATES = [
  { value: 'bulk_booking', title: 'Bulk Booking', desc: 'Multiple rooms/guests billed together under one invoice.' },
  { value: 'guest_entry', title: 'Guest Entry', desc: 'Single guest stay, itemized per charge.' }
];

class Invoices extends React.Component {
  state = {
    invoices: [],
    companies: [],
    hotels: [],
    registrations: [],
    loading: true,
    error: '',
    saving: false,
    showModal: false,
    wizardStep: 1,
    form: EMPTY_FORM,
    editingInvoiceId: null
  };

  componentDidMount() {
    this.loadAll();
    this.restorePendingInvoiceIfAny();
  }

  restorePendingInvoiceIfAny = () => {
    const params = new URLSearchParams(window.location.search);
    const newHotelId = params.get('newHotelId');
    if (!newHotelId) return;

    let savedForm = null;
    try {
      const raw = sessionStorage.getItem('kts_pending_invoice_form');
      if (raw) savedForm = JSON.parse(raw);
    } catch (err) {
      savedForm = null;
    }

    sessionStorage.removeItem('kts_pending_invoice_form');
    window.history.replaceState({}, '', '/invoices');

    this.setState({
      showModal: true,
      wizardStep: 4,
      editingInvoiceId: null,
      form: {
        ...(savedForm || EMPTY_FORM),
        hotel_id: newHotelId
      }
    });
  };

  loadAll = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const [invoicesRes, companiesRes, hotelsRes, regRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/companies'),
        api.get('/hotels'),
        api.get('/company-states')
      ]);
      this.setState({
        invoices: invoicesRes.data,
        companies: companiesRes.data,
        hotels: hotelsRes.data,
        registrations: regRes.data,
        loading: false
      });
    } catch (err) {
      this.setState({ error: 'Failed to load invoices.', loading: false });
    }
  };

  openModal = () => this.setState({
    showModal: true, wizardStep: 1, form: EMPTY_FORM, error: '', editingInvoiceId: null
  });

  openEditModal = async (invoice) => {
    this.setState({ error: '' });
    try {
      const res = await api.get(`/invoices/${invoice.id}`);
      const inv = res.data;
      const guests = (inv.guests || []).map((g) => ({
        guest_name: g.guest_name || '',
        check_in: g.check_in ? String(g.check_in).slice(0, 10) : '',
        check_out: g.check_out ? String(g.check_out).slice(0, 10) : '',
        no_of_rooms: g.no_of_rooms || 1,
        no_of_nights: g.no_of_nights || 1,
        confirmation_no: g.confirmation_no || '',
        basic_amount: g.basic_amount || 0,
        commission_percent: g.commission_percent || 0
      }));

      this.setState({
        showModal: true,
        wizardStep: 4,
        editingInvoiceId: inv.id,
        form: {
          invoice_date: inv.invoice_date ? String(inv.invoice_date).slice(0, 10) : new Date().toISOString().slice(0, 10),
          company_id: inv.company_id,
          registration_id: inv.registration_id || '',
          invoice_type: inv.invoice_type,
          hotel_id: inv.hotel_id,
          invoice_seq: '',
          tax_percent: inv.tax_percent,
          notes: inv.notes || '',
          guests: guests.length ? guests : [inv.invoice_type === 'bulk_booking' ? EMPTY_BOOKING() : EMPTY_GUEST()]
        }
      });
    } catch (err) {
      this.setState({ error: 'Failed to load invoice for editing.' });
    }
  };

  closeModal = () => this.setState({ showModal: false, editingInvoiceId: null });

  goBack = () => this.setState({ wizardStep: Math.max(1, this.state.wizardStep - 1), error: '' });

  handleAddHotelClick = () => {
    // Preserve the in-progress invoice wizard state before leaving the
    // page, so it can be restored exactly as-is once the new hotel is
    // saved and we're redirected back here.
    try {
      sessionStorage.setItem('kts_pending_invoice_form', JSON.stringify(this.state.form));
    } catch (err) {
      // sessionStorage unavailable (e.g. private browsing) — proceed anyway,
      // the user will just need to re-pick company/state/template on return.
    }
    window.location.href = `/hotels?openAdd=1&returnTo=${encodeURIComponent('/invoices')}`;
  };

  selectCompany = (company) => {
    this.setState({
      form: { ...this.state.form, company_id: company.id, registration_id: '', invoice_type: '' },
      wizardStep: 2,
      error: ''
    });
  };

  selectRegistration = (reg) => {
    this.setState({
      form: { ...this.state.form, registration_id: reg ? reg.id : '' },
      wizardStep: 3,
      error: ''
    });
  };

  selectTemplate = (value) => {
    this.setState({
      form: {
        ...this.state.form,
        invoice_type: value,
        guests: [value === 'bulk_booking' ? EMPTY_BOOKING() : EMPTY_GUEST()]
      },
      wizardStep: 4,
      error: ''
    });
  };

  handleFormChange = (e) => {
    this.setState({ form: { ...this.state.form, [e.target.name]: e.target.value } });
  };

  calcNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return null;
    const inDate = new Date(checkIn);
    const outDate = new Date(checkOut);
    const diffDays = Math.round((outDate - inDate) / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : null;
  };

  handleGuestChange = (index, field, value) => {
    const guests = [...this.state.form.guests];
    const updated = { ...guests[index], [field]: value };

    if (field === 'check_in' || field === 'check_out') {
      const nights = this.calcNights(updated.check_in, updated.check_out);
      if (nights !== null) updated.no_of_nights = nights;
    }

    guests[index] = updated;
    this.setState({ form: { ...this.state.form, guests } });
  };

  addGuest = () => {
    const newRow = this.state.form.invoice_type === 'bulk_booking' ? EMPTY_BOOKING() : EMPTY_GUEST();
    this.setState({ form: { ...this.state.form, guests: [...this.state.form.guests, newRow] } });
  };

  removeGuest = (index) => {
    const guests = this.state.form.guests.filter((_, i) => i !== index);
    const fallback = this.state.form.invoice_type === 'bulk_booking' ? EMPTY_BOOKING() : EMPTY_GUEST();
    this.setState({ form: { ...this.state.form, guests: guests.length ? guests : [fallback] } });
  };

  // Commission = Basic x Nights x Rooms x %Comm
  computeTotals = () => {
    const { guests, tax_percent } = this.state.form;
    const subtotal = guests.reduce((sum, g) => {
      const nights = Number(g.no_of_nights) || 1;
      const rooms = Number(g.no_of_rooms) || 1;
      const commission = (Number(g.basic_amount) || 0) * nights * rooms * ((Number(g.commission_percent) || 0) / 100);
      return sum + commission;
    }, 0);
    const taxAmount = subtotal * ((Number(tax_percent) || 0) / 100);
    const rawTotal = subtotal + taxAmount;
    const total = Math.round(rawTotal);
    return { subtotal, taxAmount, total, roundOff: total - rawTotal };
  };

  getTaxType() {
    const reg = this.getSelectedRegistration();
    const hotel = this.state.hotels.find((h) => String(h.id) === String(this.state.form.hotel_id));
    if (!reg || !hotel) return null;
    return String(reg.state_id) === String(hotel.state_id) ? 'cgst_sgst' : 'igst';
  }

  handleSubmit = async (e) => {
    e.preventDefault();
    const { form, editingInvoiceId } = this.state;

    if (!form.hotel_id) {
      this.setState({ error: 'Select a hotel before saving the invoice.' });
      return;
    }
    if (!editingInvoiceId && (!form.invoice_seq || !String(form.invoice_seq).trim())) {
      this.setState({ error: 'Enter the invoice number.' });
      return;
    }
    if (form.guests.some((g) => !g.guest_name || Number(g.basic_amount) <= 0)) {
      const msg = form.invoice_type === 'bulk_booking'
        ? 'Every booking row needs details and an amount greater than zero.'
        : 'Every guest row needs a name and a basic amount greater than zero.';
      this.setState({ error: msg });
      return;
    }

    this.setState({ saving: true, error: '' });
    try {
      if (editingInvoiceId) {
        await api.put(`/invoices/${editingInvoiceId}`, form);
        this.closeModal();
        this.loadAll();
      } else {
        const res = await api.post('/invoices', form);
        this.closeModal();
        this.loadAll();
        this.downloadPdf(res.data);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to save invoice.';
      this.setState({ error: message });
    } finally {
      this.setState({ saving: false });
    }
  };

  handleDelete = async (invoice) => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${invoice.id}`);
      this.loadAll();
    } catch (err) {
      this.setState({ error: 'Failed to delete invoice.' });
    }
  };

  downloadPdf = async (invoice) => {
    try {
      const res = await api.get(`/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));

      // The server builds the real filename from hotel name + first guest
      // name; parse it out of Content-Disposition rather than guessing it
      // here, since this component doesn't always have that data on hand
      // (e.g. right after creating an invoice, the response is just ids/totals).
      let filename = `${invoice.invoice_number || 'invoice'}.pdf`;
      const disposition = res.headers?.['content-disposition'];
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) filename = match[1];
      }

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      this.setState({ error: 'Failed to generate PDF.' });
    }
  };

  getSelectedCompany() {
    return this.state.companies.find((c) => String(c.id) === String(this.state.form.company_id));
  }

  getSelectedRegistration() {
    return this.state.registrations.find((r) => String(r.id) === String(this.state.form.registration_id));
  }

  renderWizardSteps() {
    const { wizardStep } = this.state;
    return (
      <div className="wizard-steps">
        {STEPS.map((label, i) => {
          const stepNum = i + 1;
          const status = stepNum < wizardStep ? 'done' : stepNum === wizardStep ? 'active' : '';
          return (
            <React.Fragment key={label}>
              <div className={`wizard-step ${status}`}>
                <span className="wizard-step-dot">{stepNum < wizardStep ? '✓' : stepNum}</span>
                {label}
              </div>
              {stepNum < STEPS.length && <div className="wizard-step-line" />}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  renderSummary() {
    const { wizardStep } = this.state;
    const company = this.getSelectedCompany();
    const reg = this.getSelectedRegistration();
    const template = TEMPLATES.find((t) => t.value === this.state.form.invoice_type);
    if (wizardStep === 1 || !company) return null;

    return (
      <div className="wizard-summary">
        <span className="chip">Company: {company.name}</span>
        {reg && wizardStep > 2 && <span className="chip">State: {reg.state_name}</span>}
        {template && wizardStep > 3 && <span className="chip">Template: {template.title}</span>}
      </div>
    );
  }

  renderStepCompany() {
    const { companies, form } = this.state;
    if (companies.length === 0) {
      return <div className="empty-state">No companies yet — add one under Companies first.</div>;
    }
    return (
      <div className="option-grid">
        {companies.map((c) => (
          <div
            key={c.id}
            className={`option-card ${String(form.company_id) === String(c.id) ? 'selected' : ''}`}
            onClick={() => this.selectCompany(c)}
          >
            {c.logo_path ? (
              <img src={assetUrl(c.logo_path)} alt="" />
            ) : (
              <div className="option-title">🏢</div>
            )}
            <span className="option-title">{c.name}</span>
          </div>
        ))}
      </div>
    );
  }

  renderStepState() {
    const { registrations, form } = this.state;
    const companyRegs = registrations.filter((r) => String(r.company_id) === String(form.company_id));

    if (companyRegs.length === 0) {
      return (
        <div className="empty-state">
          <div className="glyph">—</div>
          No GST registrations found for this company.
          <div style={{ marginTop: 16 }}>
            <button className="btn btn-outline" onClick={() => this.selectRegistration(null)}>
              Continue without a state
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="option-grid">
        {companyRegs.map((r) => (
          <div
            key={r.id}
            className={`option-card ${String(form.registration_id) === String(r.id) ? 'selected' : ''}`}
            onClick={() => this.selectRegistration(r)}
          >
            <span className="option-title">{r.state_name}</span>
            <span className="option-sub">{r.gst_number}</span>
            {r.invoice_code && <span className="option-sub">Code: {r.invoice_code}</span>}
          </div>
        ))}
      </div>
    );
  }

  renderStepTemplate() {
    const { form } = this.state;
    return (
      <div className="option-grid">
        {TEMPLATES.map((t) => (
          <div
            key={t.value}
            className={`option-card template-card ${form.invoice_type === t.value ? 'selected' : ''}`}
            onClick={() => this.selectTemplate(t.value)}
          >
            <span className="option-title">{t.title}</span>
            <span className="option-desc">{t.desc}</span>
          </div>
        ))}
      </div>
    );
  }

  renderStepDetails() {
    const { hotels, form, saving, editingInvoiceId } = this.state;
    const { subtotal, taxAmount, total, roundOff } = this.computeTotals();
    const taxType = this.getTaxType();
    const reg = this.getSelectedRegistration();
    const invoicePrefix = buildInvoiceNumberPrefix(form.invoice_date, reg?.invoice_code);
    const editingInvoice = editingInvoiceId ? this.state.invoices.find((i) => i.id === editingInvoiceId) : null;

    return (
      <form onSubmit={this.handleSubmit}>
        <div className="form-row">
          <div className="field">
            <label>Invoice Date</label>
            <input
              type="date"
              name="invoice_date"
              value={form.invoice_date}
              onChange={this.handleFormChange}
              required
            />
          </div>
          <div className="field">
            <label>Hotel</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select name="hotel_id" value={form.hotel_id} onChange={this.handleFormChange} required style={{ flex: 1 }}>
                <option value="">Select hotel</option>
                {hotels.map((h) => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-outline"
                style={{ flexShrink: 0, padding: '0 14px' }}
                onClick={this.handleAddHotelClick}
                title="Add a new hotel — you'll be brought back here afterwards"
              >
                + Add Hotel
              </button>
            </div>
          </div>
        </div>

        <div className="field">
          <label>Invoice Number</label>
          {editingInvoiceId ? (
            <input value={editingInvoice?.invoice_number || ''} disabled className="mono" />
          ) : (
            <div className="invoice-number-row">
              <span className="invoice-number-prefix" title="Auto-generated from state code, financial year, month, and date">
                {invoicePrefix || '—'}
              </span>
              <input
                className="invoice-number-suffix"
                value={form.invoice_seq}
                onChange={this.handleFormChange}
                name="invoice_seq"
                placeholder="e.g. 1"
                required
              />
            </div>
          )}
        </div>

        {form.hotel_id && (
          <div className="wizard-summary" style={{ marginBottom: 4 }}>
            <span className="chip">
              Tax: {taxType === 'cgst_sgst' ? `CGST ${form.tax_percent / 2}% + SGST ${form.tax_percent / 2}%` : taxType === 'igst' ? `IGST ${form.tax_percent}%` : 'Select a state registration to determine tax type'}
            </span>
          </div>
        )}

        {form.invoice_type === 'bulk_booking' ? (
          <>
            <label style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>Booking Entry</label>
            <div style={{ marginTop: 8 }}>
              {form.guests.map((g, i) => (
                <div key={i} className="booking-row">
                  <div className="field" style={{ flex: 1 }}>
                    <label>Booking Details</label>
                    <textarea
                      value={g.guest_name}
                      onChange={(e) => this.handleGuestChange(i, 'guest_name', e.target.value)}
                      placeholder="e.g. 12 rooms, Kabra group booking, 3N"
                      rows={3}
                      required
                    />
                  </div>
                  <div className="field booking-amount-field" style={{ width: 160 }}>
                    <label>Amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={g.basic_amount}
                      onChange={(e) => this.handleGuestChange(i, 'basic_amount', e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="icon-btn booking-row-remove"
                    onClick={() => this.removeGuest(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="btn btn-outline" style={{ marginTop: 8 }} onClick={this.addGuest}>
              + Add Booking
            </button>
          </>
        ) : (
          <>
            <label style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>Guest Entries</label>
            <div className="guest-table-scroll">
              <table className="data-table line-items-table guest-table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th style={{ width: 150 }}>Guest Name</th>
                    <th style={{ width: 118 }}>Check In</th>
                    <th style={{ width: 118 }}>Check Out</th>
                    <th style={{ width: 52 }}>Rooms</th>
                    <th style={{ width: 52 }}>Nights</th>
                    <th style={{ width: 120 }}>Confirmation No</th>
                    <th style={{ width: 85 }}>Basic</th>
                    <th style={{ width: 65 }}>% Comm</th>
                    <th style={{ width: 90 }}>Comm Amt</th>
                    <th style={{ width: 32 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {form.guests.map((g, i) => {
                    const nights = Number(g.no_of_nights) || 1;
                    const rooms = Number(g.no_of_rooms) || 1;
                    const commission = (Number(g.basic_amount) || 0) * nights * rooms * ((Number(g.commission_percent) || 0) / 100);
                    return (
                      <tr key={i}>
                        <td>
                          <input
                            value={g.guest_name}
                            onChange={(e) => this.handleGuestChange(i, 'guest_name', e.target.value)}
                            placeholder="Guest name"
                            required
                          />
                        </td>
                        <td>
                          <input type="date" value={g.check_in} onChange={(e) => this.handleGuestChange(i, 'check_in', e.target.value)} />
                        </td>
                        <td>
                          <input type="date" value={g.check_out} onChange={(e) => this.handleGuestChange(i, 'check_out', e.target.value)} />
                        </td>
                        <td>
                          <input type="number" min="1" value={g.no_of_rooms} onChange={(e) => this.handleGuestChange(i, 'no_of_rooms', e.target.value)} />
                        </td>
                        <td>
                          <input type="number" value={g.no_of_nights} disabled title="Calculated from check-in / check-out" />
                        </td>
                        <td>
                          <input value={g.confirmation_no} onChange={(e) => this.handleGuestChange(i, 'confirmation_no', e.target.value)} />
                        </td>
                        <td>
                          <input type="number" min="0" step="0.01" value={g.basic_amount} onChange={(e) => this.handleGuestChange(i, 'basic_amount', e.target.value)} />
                        </td>
                        <td>
                          <input type="number" min="0" step="0.01" value={g.commission_percent} onChange={(e) => this.handleGuestChange(i, 'commission_percent', e.target.value)} />
                        </td>
                        <td className="amount">₹{commission.toFixed(2)}</td>
                        <td>
                          <button type="button" className="icon-btn" onClick={() => this.removeGuest(i)}>✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button type="button" className="btn btn-outline" style={{ marginTop: 8 }} onClick={this.addGuest}>
              + Add Guest
            </button>
          </>
        )}

        <div className="form-row" style={{ marginTop: 16 }}>
          <div className="field">
            <label>GST %</label>
            <input
              type="number"
              min="0"
              step="0.01"
              name="tax_percent"
              value={form.tax_percent}
              onChange={this.handleFormChange}
            />
          </div>
          <div className="field">
            <label>Notes</label>
            <input name="notes" value={form.notes} onChange={this.handleFormChange} />
          </div>
        </div>

        <div className="totals-block">
          <div>Subtotal{form.invoice_type === 'bulk_booking' ? '' : ' (Commission)'}: ₹{subtotal.toFixed(2)}</div>
          {taxType === 'cgst_sgst' ? (
            <>
              <div>CGST @ {(form.tax_percent / 2).toFixed(1)}%: ₹{(taxAmount / 2).toFixed(2)}</div>
              <div>SGST @ {(form.tax_percent / 2).toFixed(1)}%: ₹{(taxAmount / 2).toFixed(2)}</div>
            </>
          ) : (
            <div>IGST @ {Number(form.tax_percent).toFixed(1)}%: ₹{taxAmount.toFixed(2)}</div>
          )}
          {Math.abs(roundOff) > 0.001 && <div>Round off: ₹{roundOff.toFixed(2)}</div>}
          <div className="grand">Grand Total: ₹{total.toFixed(2)}</div>
        </div>

        <div className="wizard-nav">
          {editingInvoiceId ? (
            <button type="button" className="btn btn-outline" onClick={this.closeModal}>Cancel</button>
          ) : (
            <button type="button" className="btn btn-outline" onClick={this.goBack}>← Back</button>
          )}
          <button type="submit" className="btn btn-brass" disabled={saving}>
            {saving ? 'Saving...' : editingInvoiceId ? 'Update Invoice' : 'Save Invoice'}
          </button>
        </div>
      </form>
    );
  }

  renderWizardBody() {
    const { wizardStep } = this.state;
    if (wizardStep === 1) return this.renderStepCompany();
    if (wizardStep === 2) return this.renderStepState();
    if (wizardStep === 3) return this.renderStepTemplate();
    return this.renderStepDetails();
  }

  render() {
    const { invoices, loading, error, showModal, wizardStep, editingInvoiceId } = this.state;

    return (
      <Layout>
        <div className="page-header">
          <div>
            <h1 className="page-title">Generate Invoice</h1>
            <p className="page-sub">Create and track invoices for hotels and companies</p>
          </div>
          <button className="btn btn-brass" onClick={this.openModal}>+ New Invoice</button>
        </div>

        {error && !showModal && <div className="error-banner">{error}</div>}

        <div className="card">
          {loading ? (
            <p>Loading...</p>
          ) : invoices.length === 0 ? (
            <div className="empty-state">
              <div className="glyph">—</div>
              No invoices generated yet.
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Date</th>
                  <th>Company</th>
                  <th>Hotel</th>
                  <th>Total</th>
                  <th className="actions-col">Action</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td className="mono" data-label="Invoice #">{inv.invoice_number}</td>
                    <td data-label="Date">{inv.invoice_date}</td>
                    <td data-label="Company">{inv.company_name || '—'}</td>
                    <td data-label="Hotel">{inv.hotel_name || '—'}</td>
                    <td className="amount" data-label="Total">₹{Number(inv.total_amount).toFixed(2)}</td>
                    <td className="actions-cell" data-label="Action">
                      <div className="row-actions">
                        <button
                          type="button"
                          className="btn btn-outline"
                          style={{ padding: '4px 12px', fontSize: 12 }}
                          onClick={() => this.downloadPdf(inv)}
                        >
                          PDF
                        </button>
                        <button className="action-btn edit" title="Edit" onClick={() => this.openEditModal(inv)}>
                          <EditIcon />
                        </button>
                        <button className="action-btn delete" title="Delete" onClick={() => this.handleDelete(inv)}>
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <div className="modal-backdrop" onClick={this.closeModal}>
            <div className="modal" style={{ width: 1100, maxWidth: '95vw' }} onClick={(e) => e.stopPropagation()}>
              <h2>{editingInvoiceId ? 'Edit Invoice' : 'New Invoice'}</h2>

              {!editingInvoiceId && this.renderWizardSteps()}
              {!editingInvoiceId && this.renderSummary()}

              {error && <div className="error-banner">{error}</div>}

              <div className="wizard-body">{this.renderWizardBody()}</div>

              {!editingInvoiceId && wizardStep < 4 && wizardStep > 1 && (
                <div className="wizard-nav">
                  <button type="button" className="btn btn-outline" onClick={this.goBack}>← Back</button>
                  <button type="button" className="btn btn-outline" onClick={this.closeModal}>Cancel</button>
                </div>
              )}
              {!editingInvoiceId && wizardStep === 1 && (
                <div className="wizard-nav" style={{ justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-outline" onClick={this.closeModal}>Cancel</button>
                </div>
              )}
            </div>
          </div>
        )}
      </Layout>
    );
  }
}

export default Invoices;